/**
 * useSettings - Persistent app settings backed by localStorage.
 *
 * Backed by a MODULE-LEVEL singleton store + useSyncExternalStore so every
 * component calling useSettings() shares ONE source of truth. A setter in one
 * component (e.g. the speed pill in ReviewView) re-renders every other caller
 * (e.g. the TTS horns in CardBack) immediately. The earlier per-instance
 * useState copy broke this — the pill updated localStorage but already-mounted
 * siblings kept their stale snapshot, so the pill "didn't control" the horns.
 *
 * Fields:
 * - maxCardsPerReview: max cards per review session (5–100, default 20)
 * - languagePair: active language pair ID (e.g. 'en-zh', 'en-es')
 * - learningContext: free-text learning goal that tunes AI generation (default '')
 * - playbackSpeed: media/TTS speed (one of SPEEDS, default 1)
 */

import { useCallback, useSyncExternalStore } from 'react'
import { clampSpeed, DEFAULT_SPEED } from '@/lib/playback-speed'

const STORAGE_KEY = 'settings'

const DEFAULTS = {
  maxCardsPerReview: 20,
  languagePair: 'en-zh',
  onboarded: false,
  learningContext: '',
} as const

const MIN_CARDS = 5
const MAX_CARDS = 100

type Settings = Record<string, unknown>

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

// Singleton store — one snapshot shared across all useSettings() callers.
let store: Settings = loadSettings()
const subscribers = new Set<() => void>()

function commit(updater: (prev: Settings) => Settings): void {
  store = updater(store)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch {
    /* quota / private-mode — in-memory store still authoritative for the session */
  }
  subscribers.forEach(fn => fn())
}

function subscribe(cb: () => void): () => void {
  // Re-hydrate from localStorage on the 0→1 subscriber transition. Production
  // never needs this (commit keeps store + localStorage in lockstep), but tests
  // seed localStorage directly between unmounts — without it the singleton store
  // would hold the previous test's snapshot. The cleanup() between tests unmounts
  // everything, so the next mount trips this gate and re-reads.
  if (subscribers.size === 0) store = loadSettings()
  subscribers.add(cb)
  return () => {
    subscribers.delete(cb)
  }
}

function getSnapshot(): Settings {
  return store
}

export interface UseSettingsReturn {
  maxCardsPerReview: number
  setMaxCardsPerReview: (value: number) => void
  languagePair: string
  setLanguagePair: (value: string) => void
  onboarded: boolean
  setOnboarded: (value: boolean) => void
  learningContext: string
  setLearningContext: (value: string) => void
  playbackSpeed: number
  setPlaybackSpeed: (value: number) => void
}

export function useSettings(): UseSettingsReturn {
  const settings = useSyncExternalStore(subscribe, getSnapshot)

  const maxCardsPerReview = typeof settings.maxCardsPerReview === 'number'
    ? Math.min(Math.max(settings.maxCardsPerReview, MIN_CARDS), MAX_CARDS)
    : DEFAULTS.maxCardsPerReview

  const setMaxCardsPerReview = useCallback((value: number) => {
    const clamped = Math.min(Math.max(value, MIN_CARDS), MAX_CARDS)
    commit(prev => ({ ...prev, maxCardsPerReview: clamped }))
  }, [])

  const languagePair = typeof settings.languagePair === 'string'
    ? settings.languagePair
    : DEFAULTS.languagePair

  const setLanguagePair = useCallback((value: string) => {
    commit(prev => ({ ...prev, languagePair: value }))
  }, [])

  const learningContext = typeof settings.learningContext === 'string'
    ? settings.learningContext
    : DEFAULTS.learningContext

  const setLearningContext = useCallback((value: string) => {
    commit(prev => ({ ...prev, learningContext: value }))
  }, [])

  const playbackSpeed = clampSpeed(settings.playbackSpeed ?? DEFAULT_SPEED)

  const setPlaybackSpeed = useCallback((value: number) => {
    const clamped = clampSpeed(value)
    commit(prev => ({ ...prev, playbackSpeed: clamped }))
  }, [])

  // Explicit onboarded flag takes priority; otherwise existing users (who have languagePair saved) are considered onboarded
  const onboarded = settings.onboarded === true
    || (settings.onboarded === undefined && 'languagePair' in settings)

  const setOnboarded = useCallback((value: boolean) => {
    commit(prev => ({ ...prev, onboarded: value }))
  }, [])

  return { maxCardsPerReview, setMaxCardsPerReview, languagePair, setLanguagePair, onboarded, setOnboarded, learningContext, setLearningContext, playbackSpeed, setPlaybackSpeed }
}
